import { useAtom } from "jotai";
import { useRouter } from "next/router";
import { useEffect } from "react";

import { homePath } from "@/global-state/agency-app-path";
import { authInitializingAtom, authUserAtom, idTokenAtom } from "@/global-state/jotai-atom";

export type AuthenticatedSession = {
  /** 認証状態の判定がまだ終わっていない */
  initializing: boolean;
  /** 誰かがログイン済み */
  isAuthenticated: boolean;
  /** ログイン中のアカウントのメールアドレス */
  email: string | null;
};

/** ログイン中かどうかだけを返す。リダイレクトはしない */
export const useAuthenticatedSession = (): AuthenticatedSession => {
  const [initializing] = useAtom(authInitializingAtom);
  const [authUser] = useAtom(authUserAtom);
  const [idToken] = useAtom(idTokenAtom);

  return {
    initializing,
    isAuthenticated: !initializing && !!authUser?.id && !!idToken,
    email: authUser?.email ?? null,
  };
};

/**
 * ログイン済みならホームへ飛ばす。
 *
 * ログインフォーム（`/`）とパスワード再設定の申請フォーム（`/auth/password-reset`）のように、
 * 「ログイン中なら用が無い」画面にだけ使う。招待受諾（`/auth/invitation`）と
 * パスワード再設定の確定（`/auth/reset-password-confirm`）はメール由来の一度きりのコードを
 * 持って開かれるため、無言で飛ばすとコードが失われる。そちらは
 * `useAuthenticatedSession` で状態だけ受け取り、案内を出すこと（#1520）。
 */
export const useRedirectIfAuthenticated = () => {
  const router = useRouter();
  const { initializing, isAuthenticated } = useAuthenticatedSession();

  useEffect(() => {
    if (initializing) return;
    if (isAuthenticated) {
      router.push(homePath);
    }
  }, [initializing, isAuthenticated, router]);
};
